import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { api, ApiError } from '@/lib/api'

export function LoginPage() {
  const [searchParams] = useSearchParams()

  // Prefer the fragment (#t=…) — it is never sent to servers, proxies, or logs.
  // Fall back to the legacy query param (?t=…) for backward compatibility with
  // links already sent before the fragment-based flow was deployed.
  const hashToken = new URLSearchParams(window.location.hash.slice(1)).get('t')
  const token = hashToken ?? searchParams.get('t')

  const [error, setError] = useState('')
  const [consuming, setConsuming] = useState(!!token)

  useEffect(() => {
    if (!token) return
    // Scrub the token from the address bar BEFORE the network request so it
    // cannot leak via the Referer header on any redirect that follows.
    window.history.replaceState(null, '', window.location.pathname)
    api
      .consumeLoginToken(token)
      .then(() => {
        // Full page reload so AuthProvider re-fetches /api/v1/me with the new cookie.
        // React Router navigate() would land on /dashboard while status is still
        // 'unauthenticated' (race), causing an immediate redirect back to /login.
        window.location.replace('/dashboard')
      })
      .catch((err) => {
        setError(
          err instanceof ApiError && err.status === 401
            ? 'Ссылка устарела или уже была использована. Запросите новую через бота.'
            : 'Ошибка входа. Попробуйте ещё раз.',
        )
        setConsuming(false)
      })
  }, []) // run once on mount

  if (consuming) {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: 'var(--bg)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div style={{ textAlign: 'center', color: 'var(--ink-2)' }}>
          <div
            style={{
              width: 32,
              height: 32,
              border: '3px solid var(--border)',
              borderTopColor: 'var(--accent)',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite',
              margin: '0 auto 12px',
            }}
          />
          <p style={{ fontSize: 14 }}>Выполняется вход…</p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    )
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--bg)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0 1rem',
      }}
    >
      <div style={{ width: '100%', maxWidth: 420 }}>
        <div
          style={{
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-xl)',
            padding: '2.5rem',
            boxShadow: 'var(--shadow-md)',
          }}
        >
          {/* Logo */}
          <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
            <span
              style={{
                fontWeight: 700,
                fontSize: 24,
                letterSpacing: '-0.03em',
                color: 'var(--ink)',
              }}
            >
              SENSOR
            </span>
            <p style={{ marginTop: 6, fontSize: 13, color: 'var(--ink-2)' }}>
              Веб-панель управления лидами
            </p>
          </div>

          {error ? (
            /* Error state */
            <div>
              <p
                style={{
                  fontSize: 14,
                  color: '#D94F4F',
                  marginBottom: '1.25rem',
                  lineHeight: 1.5,
                }}
              >
                {error}
              </p>
              <a
                href="https://t.me/sensor_tg_bot"
                className="btn btn--ghost btn--sm"
                target="_blank"
                rel="noopener noreferrer"
              >
                Открыть @sensor_tg_bot
              </a>
            </div>
          ) : (
            /* Default: no token — instruct user */
            <div>
              <div
                style={{
                  background: 'oklch(0.97 0.03 195)',
                  border: '1px solid oklch(0.90 0.05 195)',
                  borderRadius: 'var(--radius-lg)',
                  padding: '1.25rem',
                  marginBottom: '1.25rem',
                }}
              >
                <p
                  style={{
                    fontWeight: 600,
                    fontSize: 14,
                    color: 'var(--ink)',
                    marginBottom: 6,
                  }}
                >
                  Войдите через Telegram-бота
                </p>
                <p style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.5 }}>
                  Откройте{' '}
                  <a
                    href="https://t.me/sensor_tg_bot"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: 'var(--accent)',
                      textDecoration: 'underline',
                      textUnderlineOffset: 2,
                    }}
                  >
                    @sensor_tg_bot
                  </a>{' '}
                  и нажмите кнопку <strong>«Веб-панель»</strong>. Бот пришлёт одноразовую
                  ссылку для входа.
                </p>
              </div>
              <p style={{ textAlign: 'center', fontSize: 12, color: 'var(--muted)' }}>
                Ссылка действительна 5 минут и используется один раз
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
